import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import "../assets/css/App.css";
const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000/api";
// Ensure no double slashes in URL construction
const API_URL = `${API_BASE.replace(/\/$/, '')}/auth`;

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!token) {
      setMessage("❌ Invalid reset link");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    const minLengthOk = password.length >= 15;
    const minUppercaseOk = /[A-Z]/.test(password);
    const minSymbolsOk = /[^A-Za-z0-9]/.test(password);

    if (!minLengthOk || !minUppercaseOk || !minSymbolsOk) {
      setMessage("❌ Password must be at least 15 characters, include 1 uppercase letter and 1 symbol.");
      return;
    }

    if (password !== confirmPassword) {
      setMessage("❌ Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const resetUrl = `${API_URL}/reset-password/${token}`;
      console.log('Calling reset password endpoint:', resetUrl);
      
      const res = await fetch(resetUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
        credentials: "include",
      });

      console.log('Response status:', res.status, res.statusText);

      // Handle 404 specifically
      if (res.status === 404) {
        throw new Error(`Route not found. Please check if the backend is running and the endpoint ${resetUrl} exists.`);
      }

      // Check if response is JSON before parsing
      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        console.error("Non-JSON response received:", text.substring(0, 200));
        throw new Error("Server returned an invalid response. Please try again later.");
      }

      const data = await res.json();
      
      if (!res.ok) {
        throw new Error(data.error || "Failed to reset password");
      }

      setMessage("✅ Password reset successfully! Redirecting to login...");
      
      setTimeout(() => {
        navigate("/login");
      }, 2000);
    } catch (err) {
      setMessage(`❌ ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* === Animated Background === */}
      <section className="background">
        {Array(130)
          .fill("")
          .map((_, index) => (
            <span key={index}></span>
          ))}
      </section>

      {/* === Centered Reset Password Area === */}
      <div className="signin-wrapper">
        <div className="signin">
          <div className="content">
            <h2>Reset Password</h2>
            <form className="form" onSubmit={handleSubmit}>
              <div className="inputBox" style={{ position: "relative" }}>
                <input
                  type={showNew ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="New Password"
                  minLength={15}
                  style={{ paddingRight: "3rem" }}
                />
                <i>New Password</i>
                <button
                  type="button"
                  onClick={() => setShowNew((s) => !s)}
                  aria-label={showNew ? "Hide new password" : "Show new password"}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "inherit",
                    cursor: "pointer",
                    fontSize: "2rem",
                    padding: 0,
                  }}
                >
                  {showNew ? "🙈" : "👁️"}
                </button>
              </div>

              <div style={{ display: "flex", gap: "12px", alignItems: "center", marginTop: "8px", color: "#fff", fontSize: "0.95em" }}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <strong style={{ color: password.length >= 15 ? "#4caf50" : "#ff6b6b" }}>{password.length >= 15 ? "✅" : "❌"}</strong>
                  <span style={{ opacity: 0.95 }}>15 chars</span>
                </span>

                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <strong style={{ color: /[A-Z]/.test(password) ? "#4caf50" : "#ff6b6b" }}>{/[A-Z]/.test(password) ? "✅" : "❌"}</strong>
                  <span style={{ opacity: 0.95 }}>1 uppercase</span>
                </span>

                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                  <strong style={{ color: /[^A-Za-z0-9]/.test(password) ? "#4caf50" : "#ff6b6b" }}>{/[^A-Za-z0-9]/.test(password) ? "✅" : "❌"}</strong>
                  <span style={{ opacity: 0.95 }}>1 symbol</span>
                </span>
              </div>

              <div className="inputBox" style={{ position: "relative" }}>
                <input
                  type={showConfirm ? "text" : "password"}
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm Password"
                  minLength={15}
                  style={{ paddingRight: "3rem" }}
                />
                <i>Confirm Password</i>
                <button
                  type="button"
                  onClick={() => setShowConfirm((s) => !s)}
                  aria-label={showConfirm ? "Hide confirm password" : "Show confirm password"}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    color: "inherit",
                    cursor: "pointer",
                    fontSize: "2rem",
                    padding: 0,
                  }}
                >
                  {showConfirm ? "🙈" : "👁️"}
                </button>
              </div>

              <div className="links">
                <Link to="/login">Back to Sign In</Link>
              </div>

              <div className="inputBox">
                <input 
                  type="submit" 
                  value={loading ? "Resetting..." : "Reset Password"} 
                  disabled={loading}
                />
              </div>
            </form>

            {message && (
              <p style={{ 
                color: message.includes("✅") ? "#4caf50" : "#f44336", 
                marginTop: "10px", 
                textAlign: "center" 
              }}>
                {message}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default ResetPassword;

